import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Company } from '../../models/company.model';
import { Error } from '../../models/error.model';
import { CompanyTreasuryAccounts } from '../../models/company.treasury.accounts.model';
import { Message } from '../../models/message.model';
import { authToken } from '../../scripts/authentication.script';

export class TreasuryAccountDA {
  static apiResponse: ApiResponse

  public static async getTreasuryAccounts(
    id_company: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyTreasuryAccounts: CompanyTreasuryAccounts | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/treasury.accounts.data.json').then(response => { companyTreasuryAccounts = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyTreasuryAccounts!.id_company === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyTreasuryAccounts
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/treasury-accounts`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        const raw = response.data?.data ?? response.data
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: {
            id_company,
            treasury_accounts: Array.isArray(raw) ? raw.map((item: any) => {
              const currencyCode = typeof item.currency === 'string' ? item.currency : item.currency?.code ?? ''
              const currencyObj = typeof item.currency === 'string'
                ? { code: item.currency, name: item.currency, symbol: item.currency }
                : item.currency ?? { code: '', name: '', symbol: '' }

              let accountingAccount = item.accounting_account
              if (!accountingAccount || typeof accountingAccount === 'string') {
                accountingAccount = {
                  id: item.id_account ?? item.accounting_account ?? '',
                  code: item.account_code ?? '',
                  name: item.account_name ?? {},
                  currency: currencyObj,
                  type: item.type ?? '',
                  level: 0,
                  coefficient: 0,
                  available: true,
                  id_parent: '',
                  id_mirror_account: '',
                  children: []
                }
              } else {
                if (typeof accountingAccount.currency === 'string') {
                  accountingAccount = {
                    ...accountingAccount,
                    currency: { code: accountingAccount.currency, name: accountingAccount.currency, symbol: accountingAccount.currency }
                  }
                }
                if (!accountingAccount.currency) {
                  accountingAccount.currency = currencyObj
                }
              }

              const holder = item.account_holder ?? {}
              const holderAddress = holder.address ?? {}

              return {
                ...item,
                id: item.id ?? item._id ?? '',
                currency: currencyCode,
                accounting_account: accountingAccount,
                iban: item.iban ?? '',
                bban: item.bban ?? '',
                bic: item.bic ?? '',
                membership: item.membership ?? '',
                qr_iban: item.qr_iban ?? '',
                reference: item.reference ?? '',
                is_reference: item.is_reference ?? false,
                default: item.default ?? false,
                account_holder: {
                  full_name: holder.full_name ?? '',
                  address: {
                    street: holderAddress.street ?? '',
                    number: holderAddress.number ?? '',
                    additional: holderAddress.additional ?? '',
                    administrative_area: holderAddress.administrative_area ?? '',
                    city: holderAddress.city ?? '',
                    zip_code: holderAddress.zip_code ?? '',
                    country: holderAddress.country ?? ''
                  }
                }
              }
            }) : []
          }
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postTreasuryAccount(
    id_company: string,
    type: string,
    accounting_account: string,
    name: string,
    iban: string,
    bban: string,
    bic: string,
    membership: string,
    qr_iban: string,
    reference: string,
    is_reference: boolean,
    default_treasury_account: boolean,
    account_holder: { full_name: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/treasury-accounts`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        type: type,
        name: name,
        iban: iban,
        bban: bban,
        bic: bic,
        membership: membership,
        qr_iban: qr_iban,
        reference: reference,
        is_reference: is_reference,
        default_value: default_treasury_account,
        id_account: accounting_account,
        account_holder
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putTreasuryAccount(
    id_company: string,
    id_treasury_account: string,
    name: string,
    iban: string,
    bban: string,
    bic: string,
    membership: string,
    qr_iban: string,
    reference: string,
    is_reference: boolean,
    account_holder: { full_name: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let treasuryAccounts: TreasuryAccount[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/treasury.accounts.data.json').then(response => { treasuryAccounts = response.data.treasury_accounts }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (treasuryAccounts!.some(item => item.id === id_treasury_account)) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/treasury-accounts/${id_treasury_account}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        name: name,
        iban: iban,
        bban: bban,
        bic: bic,
        membership: membership,
        qr_iban: qr_iban,
        reference: reference,
        is_reference: is_reference,
        account_holder: account_holder
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putTreasuryAccountDefault(
    id_company: string,
    id_treasury_account: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let treasuryAccounts: TreasuryAccount[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/treasury.accounts.data.json').then(response => { treasuryAccounts = response.data.treasury_accounts }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (treasuryAccounts!.some(item => item.id === id_treasury_account)) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/treasury-accounts/${id_treasury_account}/default`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.put(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteTreasuryAccount(
    id_company: string,
    id_treasury_account: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let treasuryAccounts: TreasuryAccount[] | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/treasury.accounts.data.json').then(response => { treasuryAccounts = response.data.treasury_accounts }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (treasuryAccounts!.some(item => item.id === id_treasury_account)) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/treasury-accounts/${id_treasury_account}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }
}
